import React, { useLayoutEffect } from "react";
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Award, Target, Box, CheckCircle, BarChart2, Trophy, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next'; // ADDED
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";

// Estilos para la animación focus-in-expand
const animationStyles = {
  focusInExpand: {
    animation: 'focus-in-expand 0.3s both',
    WebkitAnimation: 'focus-in-expand 0.3s both',
    overflow: 'hidden'
  }
};


const Nosotros = () => {
  const { t } = useTranslation(); // ADDED

  useLayoutEffect(() => {
    const loadCharts = () => {
      am4core.useTheme(am4themes_animated);

      function createDonut(id, data, colors) {
        try {
          const chart = am4core.create(id, am4charts.PieChart3D);
          chart.hiddenState.properties.opacity = 0;
          chart.data = data;
          chart.innerRadius = am4core.percent(40);
          chart.depth = 15;
          chart.angle = 25;

          const series = chart.series.push(new am4charts.PieSeries3D());
          series.dataFields.value = "value";
          series.dataFields.category = "feature";
          series.labels.template.disabled = true;
          series.ticks.template.disabled = true;
          series.colors.list = colors.map(c => am4core.color(c));

          chart.legend = new am4charts.Legend();
          chart.legend.position = "right";
          chart.legend.fontSize = 11;
          chart.legend.valueLabels.template.text = "{value}%";
          chart.logo.disabled = true;

          chart.responsive.enabled = false;
          chart.responsive.useDefault = false;
          chart.responsive.rules = [];

          return chart;
        } catch (error) {
          console.error(`Error creating chart ${id}:`, error);
          return null;
        }
      }

      const chartPromises = [
        createDonut("chart1", [
          { feature: t('about.chart.pvcCoating'), value: 70 },
          { feature: t('about.chart.nonWovenPolyester'), value: 30 }
        ], ["#dc143c", "#ffffff"]),

        createDonut("chart2", [
          { feature: t('about.chart.pvcCoating'), value: 40 },
          { feature: t('about.chart.wovenPolyester'), value: 30 },
          { feature: t('about.chart.polycotton'), value: 20 },
          { feature: t('about.chart.expandedPVC'), value: 10 }
        ], ["#dc143c", "#ffffff", "#f5f5f5", "#ffd700"]),

        createDonut("chart3", [
          { feature: t('about.chart.pvcCoating'), value: 60 },
          { feature: t('about.chart.wovenPolyester'), value: 30 },
          { feature: t('about.chart.expandedPVC'), value: 10 }
        ], ["#dc143c", "#ffffff", "#ffd700"]),

        createDonut("chart4", [
          { feature: t('about.chart.pvcFoam'), value: 50 },
          { feature: t('about.chart.pvcCoating'), value: 30 },
          { feature: t('about.chart.canvasOrTST'), value: 20 }
        ], ["#90EE90", "#dc143c", "#ffffff"])
      ].filter(Boolean);

      return chartPromises;
    };

    const timer = setTimeout(() => {
      const charts = loadCharts();

      return () => {
        clearTimeout(timer);
        if (charts && charts.length > 0) {
          charts.forEach(chart => {
            try {
              if (chart && !chart.isDisposed) {
                chart.dispose();
              }
            } catch (error) {
              console.error('Error cleaning up chart:', error);
            }
          });
        }
      };
    }, 300);

    return () => clearTimeout(timer);
  }, [t]); // Added t to dependency array

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="relative text-white py-20 overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <div className="absolute inset-0 hero-pattern"></div>
          <div className="absolute inset-0 bg-black/20"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-5xl font-bold mb-4">{t('about.hero.title')}</h1>
            <p className="text-xl opacity-90 max-w-2xl mx-auto">
              {t('about.hero.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              whileHover={{ y: -5, boxShadow: '0 20px 40px rgba(100, 64, 19, 0.2)' }}
              transition={{ type: 'spring', stiffness: 400, damping: 10 }}
              className="bg-gray-50 p-8 rounded-xl shadow-lg cursor-pointer transition-all duration-300"
            >
              <div className="flex items-center mb-4">
                <Target className="h-8 w-8 mr-3 text-red-600" />
                <h2 className="text-2xl font-bold text-gray-800">{t('about.mission.title')}</h2>
              </div>
              <p className="text-gray-600 leading-relaxed">
                {t('about.mission.description')}
              </p>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              whileHover={{ y: -5, boxShadow: '0 20px 40px rgba(100, 64, 19, 0.2)' }}
              transition={{ type: 'spring', stiffness: 400, damping: 10 }}
              className="bg-gray-50 p-8 rounded-xl shadow-lg cursor-pointer transition-all duration-300"
            >
              <div className="flex items-center mb-4">
                <Award className="h-8 w-8 mr-3 text-red-600" />
                <h2 className="text-2xl font-bold text-gray-800">{t('about.vision.title')}</h2>
              </div>
              <p className="text-gray-600 leading-relaxed">
                {t('about.vision.description')}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="section-materiales">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-red-600 mb-4">{t('about.productsSection.title')}</h2>
            <div className="w-20 h-1 bg-red-600 mx-auto"></div>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto"
          >
            <motion.div
              variants={fadeInUp}
              whileHover={{ y: -5, boxShadow: '0 20px 40px rgba(100, 64, 19, 0.2)' }}
              transition={{ type: 'spring', stiffness: 400, damping: 10 }}
              className="material-card"
            >
              <div className="info">
                <div className="flex items-center mb-3">
                  <svg className="h-8 w-8 mr-3 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20 9V7c0-1.1-.9-2-2-2H6c-1.1 0-2 .9-2 2v2c-1.1 0-2 .9-2 2v4c0 1.1.9 2 2 2h1v2c0 .6.4 1 1 1s1-.4 1-1v-2h8v2c0 .6.4 1 1 1s1-.4 1-1v-2h1c1.1 0 2-.9 2-2v-4c0-1.1-.9-2-2-2zM6 7h12v2H6V7zm14 8H4v-4h16v4z" />
                  </svg>
                  <h3>{t('about.productVinipiel.title')}</h3>
                </div>
                <p className="mb-3">
                  <strong>{t('about.productVinipiel.usedFor')}</strong> {t('about.productVinipiel.description')}
                </p>
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div className="text-center">
                    <div className="bg-red-100 p-2 rounded-full mb-1 mx-auto w-fit">
                      <svg className="h-6 w-6 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                    </div>
                    <p className="text-xs font-medium">{t('about.productVinipiel.featureLiquidRepellent')}</p>
                  </div>
                  <div className="text-center">
                    <div className="bg-red-100 p-2 rounded-full mb-1 mx-auto w-fit">
                      <svg className="h-6 w-6 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                      </svg>
                    </div>
                    <p className="text-xs font-medium">{t('about.productVinipiel.featureAntibacterial')}</p>
                  </div>
                </div>
                <div className="space-y-1 text-sm">
                  <p><strong>{t('about.productVinipiel.techSheet')}</strong></p>
                  <p>• {t('about.productVinipiel.techTexture')}</p>
                  <p>• {t('about.productVinipiel.techRolls')}</p>
                  <p>• {t('about.productVinipiel.techSupport')}</p>
                  <p>• {t('about.productVinipiel.techPvcCoating')}</p>
                  <p>• {t('about.productVinipiel.techColorMatching')}</p>
                  <p>• {t('about.productVinipiel.techWidth')}</p>
                  <p>• {t('about.productVinipiel.techStock')}</p>
                  <p>• {t('about.productVinipiel.techMinManufacture')}</p>
                  <p>• {t('about.productVinipiel.techDelivery')}</p>
                </div>
              </div>
              <div id="chart1" className="chart-div"></div>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              whileHover={{ y: -5, boxShadow: '0 20px 40px rgba(100, 64, 19, 0.2)' }}
              transition={{ type: 'spring', stiffness: 400, damping: 10 }}
              className="material-card"
            >
              <div className="info">
                <div className="flex items-center mb-3">
                  <svg className="h-8 w-8 mr-3 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.22.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z" />
                  </svg>
                  <h3>{t('about.productAutomotive.title')}</h3>
                </div>
                <p className="mb-3">
                  <strong>{t('about.productAutomotive.usedFor')}</strong> {t('about.productAutomotive.description')}
                </p>
                <div className="grid grid-cols-3 gap-4 mb-3">
                  <div className="text-center">
                    <div className="bg-red-100 p-2 rounded-full mb-1 mx-auto w-fit">
                      <svg className="h-6 w-6 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                      </svg>
                    </div>
                    <p className="text-xs font-medium">{t('about.productAutomotive.featureAntibacterial')}</p>
                  </div>
                  <div className="text-center">
                    <div className="bg-red-100 p-2 rounded-full mb-1 mx-auto w-fit">
                      <svg className="h-6 w-6 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                    </div>
                    <p className="text-xs font-medium">{t('about.productAutomotive.featureUvProtector')}</p>
                  </div>
                  <div className="text-center">
                    <div className="bg-red-100 p-2 rounded-full mb-1 mx-auto w-fit">
                      <svg className="h-6 w-6 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 11H7v6h2v-6zm4 0h-2v6h2v-6zm4 0h-2v6h2v-6zm2.5-9H19V1h-2v1H7V1H5v1H4.5C3.67 2 3 2.67 3 3.5v15C3 19.33 3.67 20 4.5 20h15c.83 0 1.5-.67 1.5-1.5v-15C21 2.67 20.33 2 19.5 2z" />
                      </svg>
                    </div>
                    <p className="text-xs font-medium">{t('about.productAutomotive.featureFrictionResistance')}</p>
                  </div>
                </div>
                <div className="space-y-1 text-sm">
                  <p><strong>{t('about.productAutomotive.techSheet')}</strong></p>
                  <p>• {t('about.productAutomotive.techTexture')}</p>
                  <p>• {t('about.productAutomotive.techRolls')}</p>
                  <p>• {t('about.productAutomotive.techSupport')}</p>
                  <p>• {t('about.productAutomotive.techPvcCoating')}</p>
                  <p>• {t('about.productAutomotive.techColorMatching')}</p>
                  <p>• {t('about.productAutomotive.techWidth')}</p>
                  <p>• {t('about.productAutomotive.techStock')}</p>
                  <p>• {t('about.productAutomotive.techMinManufacture')}</p>
                  <p>• {t('about.productAutomotive.techDelivery')}</p>
                </div>
              </div>
              <div id="chart2" className="chart-div"></div>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              whileHover={{ y: -5, boxShadow: '0 20px 40px rgba(100, 64, 19, 0.2)' }}
              transition={{ type: 'spring', stiffness: 400, damping: 10 }}
              className="material-card"
            >
              <div className="info">
                <div className="flex items-center mb-3">
                  <svg className="h-8 w-8 mr-3 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 1.74.5 3.37 1.41 4.84.91 1.47 2.2 2.59 3.68 3.16L12 19l1.91-2c1.48-.57 2.77-1.69 3.68-3.16C18.5 12.37 19 10.74 19 9c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                    <path d="M3 15c0 1.1.9 2 2 2h2v2c0 1.1.9 2 2 2h6c1.1 0 2-.9 2-2v-2h2c1.1 0 2-.9 2-2v-2c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v2z" />
                  </svg>
                  <h3>{t('about.productSport.title')}</h3>
                </div>
                <p className="mb-3">
                  <strong>{t('about.productSport.usedFor')}</strong> {t('about.productSport.description')}
                </p>
                <div className="grid grid-cols-3 gap-4 mb-3">
                  <div className="text-center">
                    <div className="bg-red-100 p-2 rounded-full mb-1 mx-auto w-fit">
                      <svg className="h-6 w-6 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                      </svg>
                    </div>
                    <p className="text-xs font-medium">{t('about.productSport.featureAntibacterial')}</p>
                  </div>
                  <div className="text-center">
                    <div className="bg-red-100 p-2 rounded-full mb-1 mx-auto w-fit">
                      <svg className="h-6 w-6 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                    </div>
                    <p className="text-xs font-medium">{t('about.productSport.featureUvProtector')}</p>
                  </div>
                  <div className="text-center">
                    <div className="bg-red-100 p-2 rounded-full mb-1 mx-auto w-fit">
                      <svg className="h-6 w-6 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 11H7v6h2v-6zm4 0h-2v6h2v-6zm4 0h-2v6h2v-6zm2.5-9H19V1h-2v1H7V1H5v1H4.5C3.67 2 3 2.67 3 3.5v15C3 19.33 3.67 20 4.5 20h15c.83 0 1.5-.67 1.5-1.5v-15C21 2.67 20.33 2 19.5 2z" />
                      </svg>
                    </div>
                    <p className="text-xs font-medium">{t('about.productSport.featureFrictionResistance')}</p>
                  </div>
                </div>
                <div className="space-y-1 text-sm">
                  <p><strong>{t('about.productSport.techSheet')}</strong></p>
                  <p>• {t('about.productSport.techTexture')}</p>
                  <p>• {t('about.productSport.techRolls')}</p>
                  <p>• {t('about.productSport.techSupport')}</p>
                  <p>• {t('about.productSport.techPvcCoating')}</p>
                  <p>• {t('about.productSport.techColorMatching')}</p>
                  <p>• {t('about.productSport.techWidth')}</p>
                  <p>• {t('about.productSport.techStock')}</p>
                  <p>• {t('about.productSport.techMinManufacture')}</p>
                  <p>• {t('about.productSport.techDelivery')}</p>
                </div>
              </div>
              <div id="chart3" className="chart-div"></div>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              whileHover={{ y: -5, boxShadow: '0 20px 40px rgba(100, 64, 19, 0.2)' }}
              transition={{ type: 'spring', stiffness: 400, damping: 10 }}
              className="material-card"
            >
              <div className="info">
                <div className="flex items-center mb-3">
                  <svg className="h-8 w-8 mr-3 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
                    <path d="M12 6c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z" />
                    <path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 6c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z" />
                  </svg>
                  <h3>{t('about.productBall.title')}</h3>
                </div>
                <p className="mb-3">
                  <strong>{t('about.productBall.usedFor')}</strong> {t('about.productBall.description')}
                </p>
                <p className="mb-3 text-sm">
                  {t('about.productBall.additionalDescription')}
                </p>
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div className="text-center">
                    <div className="bg-red-100 p-2 rounded-full mb-1 mx-auto w-fit">
                      <svg className="h-6 w-6 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                      </svg>
                    </div>
                    <p className="text-xs font-medium">{t('about.productBall.featureAntifungal')}</p>
                  </div>
                  <div className="text-center">
                    <div className="bg-red-100 p-2 rounded-full mb-1 mx-auto w-fit">
                      <svg className="h-6 w-6 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                    </div>
                    <p className="text-xs font-medium">{t('about.productBall.featureBounceStability')}</p>
                  </div>
                </div>
                <div className="space-y-1 text-sm">
                  <p><strong>{t('about.productBall.techSheet')}</strong></p>
                  <p>• {t('about.productBall.techTexture')}</p>
                  <p>• {t('about.productBall.techRolls')}</p>
                  <p>• {t('about.productBall.techSupport')}</p>
                  <p>• {t('about.productBall.techPvcCoating')}</p>
                  <p>• {t('about.productBall.techColorMatching')}</p>
                  <p>• {t('about.productBall.techWidth')}</p>
                  <p>• {t('about.productBall.techStock')}</p>
                  <p>• {t('about.productBall.techMinManufacture')}</p>
                  <p>• {t('about.productBall.techDelivery')}</p>
                </div>
              </div>
              <div id="chart4" className="chart-div"></div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-red-600 mb-4">{t('about.servicesSection.title')}</h2>
            <div className="w-20 h-1 bg-red-600 mx-auto"></div>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-8"
          >
            {[
              t('about.service1'),
              t('about.service2'),
              t('about.service3')
            ].map((service, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ y: -5, boxShadow: '0 20px 40px rgba(100, 64, 19, 0.2)' }}
                transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                className="bg-white p-6 rounded-xl shadow-md cursor-pointer transition-all duration-300 flex items-start"
                style={{
                  ...animationStyles.focusInExpand,
                  animationDelay: `${index * 0.1 + 0.3}s`,
                  WebkitAnimationDelay: `${index * 0.1 + 0.3}s`
                }}
              >
                <CheckCircle className="h-6 w-6 text-green-500 mt-1 mr-3 flex-shrink-0" />
                <p className="text-gray-700">{service}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-red-600 mb-8 text-center md:text-left">{t('about.achievements.title')}</h2>
              <div className="space-y-6">
                {[
                  {
                    icon: <BarChart2 className="h-8 w-8 text-red-600" />,
                    title: t('about.achievements.achievement1.title'),
                    description: '+1,200,000',
                    subtitle: t('about.achievements.achievement1.subtitle')
                  },
                  {
                    icon: <Star className="h-8 w-8 text-yellow-500" />,
                    title: t('about.achievements.achievement2.title'),
                    description: '+100',
                    subtitle: t('about.achievements.achievement2.subtitle')
                  },
                  {
                    icon: <Trophy className="h-8 w-8 text-amber-600" />,
                    title: t('about.achievements.achievement3.title'),
                    description: '2011-2019',
                    subtitle: t('about.achievements.achievement3.subtitle')
                  }
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ y: -5, boxShadow: '0 20px 40px rgba(100, 64, 19, 0.2)' }}
                    transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                    className="bg-white p-6 rounded-xl shadow-md cursor-pointer"
                  >
                    <div className="flex items-start">
                      <div className="mr-4 p-2 bg-red-50 rounded-full">
                        {item.icon}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800">{item.title}</h3>
                        <p className="text-2xl font-bold text-red-600">{item.description}</p>
                        <p className="text-gray-600">{item.subtitle}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-red-600 mb-8 text-center md:text-left">{t('about.objectives.title')}</h2>
              <div className="space-y-6">
                {[
                  {
                    title: t('about.objectives.objective1.title'),
                    description: t('about.objectives.objective1.description')
                  },
                  {
                    title: t('about.objectives.objective2.title'),
                    description: t('about.objectives.objective2.description')
                  },
                  {
                    title: t('about.objectives.objective3.title'),
                    description: t('about.objectives.objective3.description')
                  }
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.5,
                      delay: index * 0.1,
                      type: 'spring',
                      stiffness: 400,
                      damping: 10
                    }}
                    viewport={{ once: true }}
                    className="bg-white p-6 rounded-xl shadow-md cursor-pointer hover:shadow-lg transition-shadow duration-300"
                    whileHover={{ y: -5, boxShadow: '0 20px 40px rgba(100, 64, 19, 0.2)' }}
                  >
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 text-white relative overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <div className="absolute inset-0 hero-pattern"></div>
          <div className="absolute inset-0 bg-black/20"></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold mb-6"
          >
            {t('about.cta.title')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-xl mb-8 max-w-3xl mx-auto"
          >
            {t('about.cta.description')}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <div className="text-center">
              <Link
                to="/contacto"
                className="modern-button group inline-block"
              >
                {t('about.cta.button')}
              </Link>
              <style>{`
                            .modern-button {
                              display: inline-flex;
                              align-items: center;
                              justify-content: center;
                              transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
                              position: relative;
                              overflow: hidden;
                              z-index: 1;
                              color: #AE0404;
                              padding: 0.7em 1.7em;
                              cursor: pointer;
                              font-size: 18px;
                              border-radius: 0.5em;
                              background: #e8e8e8;
                              border: 1px solid #e8e8e8;
                              box-shadow: 3px 3px 6px rgba(0,0,0,0.1), -3px -3px 6px rgba(255,255,255,0.5), 0 0 10px #644013;
                            }
                            
                            .modern-button:hover {
                              padding-right: 2.5em;
                              padding-left: 1.7em;
                              color: #ffffff;
                              background: #AE0404;
                              border-color: #AE0404;
                              box-shadow: 0 0 0 1px #AE0404, 0 0 15px rgba(174, 4, 4, 0.5);
                            }
            
                            .modern-button:active {
                              color: #ffffff;
                              background: #8e0303;
                              border-color: #8e0303;
                              box-shadow: inset 4px 4px 12px rgba(0, 0, 0, 0.2), 0 0 15px rgba(174, 4, 4, 0.7);
                            }
            
                            .modern-button:before {
                              content: "";
                              position: absolute;
                              left: 50%;
                              transform: translateX(-50%) scaleY(1) scaleX(1.25);
                              top: 100%;
                              width: 140%;
                              height: 180%;
                              background-color: rgba(0, 0, 0, 0.05);
                              border-radius: 50%;
                              display: block;
                              transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
                              z-index: -1;
                            }
            
                            .modern-button:after {
                              content: "";
                              position: absolute;
                              left: 55%;
                              transform: translateX(-50%) scaleY(1) scaleX(1.45);
                              top: 180%;
                              width: 160%;
                              height: 190%;
                              background-color: #AE0404;
                              border-radius: 50%;
                              display: block;
                              transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
                              z-index: -1;
                            }
            
                            .modern-button:hover:before {
                              top: -35%;
                              background-color: #AE0404;
                              transform: translateX(-50%) scaleY(1.3) scaleX(0.8);
                            }
            
                            .modern-button:hover:after {
                              top: -45%;
                              background-color: #AE0404;
                              transform: translateX(-50%) scaleY(1.3) scaleX(0.8);
                            }
            
                            .arrow-container {
                              display: inline-flex;
                              align-items: center;
                              position: absolute;
                              right: 0.7em;
                            }
                            
                            @keyframes bounce-x {
                              0%, 100% { transform: translateX(0); }
                              50% { transform: translateX(4px); }
                            }
                            
                            .animate-bounce-x {
                              animation: bounce-x 1s infinite;
                            }
                          `}</style>
            </div>
          </motion.div>
        </div>
      </section>


    </div>
  );
};

export default Nosotros;
